"use client";

import {
	CheckIcon,
	ChevronDownIcon,
	CopyIcon,
	SparklesIcon,
} from "lucide-react";
import type { ComponentProps } from "react";
import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

export type SuggestionsProps = ComponentProps<typeof Collapsible> & {
	title?: string;
};

export const Suggestions = ({
	className,
	children,
	defaultOpen = true,
	title = "Suggestions",
	...props
}: SuggestionsProps) => (
	<Collapsible className={cn(className)} defaultOpen={defaultOpen} {...props}>
		<CollapsibleTrigger asChild className="group">
			<button
				type="button"
				className="flex w-full cursor-pointer items-center gap-2 text-muted-foreground text-sm transition-colors hover:text-foreground"
			>
				<SparklesIcon className="size-4" />
				<p className="text-sm">{title}</p>
				<ChevronDownIcon className="size-4 transition-transform group-data-[state=open]:rotate-180" />
			</button>
		</CollapsibleTrigger>
		<CollapsibleContent className="data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in">
			<div className="mt-3 flex flex-wrap items-center gap-2">{children}</div>
		</CollapsibleContent>
	</Collapsible>
);

export type SuggestionProps = Omit<ComponentProps<typeof Button>, "onClick"> & {
	suggestion: string;
	onClick?: (suggestion: string) => void;
};

export const Suggestion = ({
	suggestion,
	onClick,
	className,
	variant = "outline",
	size = "sm",
	children,
	...props
}: SuggestionProps) => {
	const [copied, setCopied] = useState(false);
	const handleClick = useCallback(async () => {
		if (onClick) {
			onClick(suggestion);
			return;
		}

		await navigator.clipboard.writeText(suggestion);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [onClick, suggestion]);

	return (
		<Button
			className={cn("cursor-pointer rounded-full px-4", className)}
			onClick={handleClick}
			size={size}
			type="button"
			variant={variant}
			{...props}
		>
			{children || suggestion}
			{onClick ? null : copied ? (
				<CheckIcon className="size-3.5 text-emerald-500" />
			) : (
				<CopyIcon className="size-3.5 text-muted-foreground" />
			)}
		</Button>
	);
};
